import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * Cookie-based auth session.
 * Backend issues an httpOnly `access_token` cookie on login; the browser
 * sends it with every `withCredentials` request. Front-end state is
 * hydrated on mount by calling /auth/me — if the cookie is missing or
 * expired the server returns 401 and we treat the user as logged out.
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(true);

    const bootstrap = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => { bootstrap(); }, [bootstrap]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore network */ }
        setUser(null);
    };

    return <AuthCtx.Provider value={{ user, checking, login, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
